import * as React from "react";

import { Box, makeStyles, Theme, useMediaQuery } from "@material-ui/core";
import * as Redux from "react-redux";

import { useTranslation } from "client/hooks";
import { useNormalizedBrandPaletteValues } from "client/hooks/useNormalizedBrandThemeValues";
import { RootState } from "client/store";

import { Step, StepName } from "./Step";
import { barHeight, getProgressPercentage, getStepStatus } from "./utils";

/***** styles ***** */
type StepItem = { stepKey: StepName; labelKey: string; mobileLabelKey: string };

const useStyles = makeStyles((theme: Theme) => {
  const { progressBar, progressBarActive } = useNormalizedBrandPaletteValues(
    theme
  );

  const progressBarThickness = theme.cash ? 2 : 1.6;

  return {
    root: {
      display: "flex",
      height: `${barHeight}px`,
      justifyContent: "center",
      position: "relative",
      width: "1.3rem",
      [theme.breakpoints.down("md")]: {
        width: "100%",
        height: theme.spacing(6),
        marginTop: theme.spacing(2),
      },
    },
    progressContainer: {
      backgroundColor: progressBar,
      height: "100%",
      position: "relative",
      width: `${progressBarThickness}px`,
      [theme.breakpoints.down("md")]: {
        height: `${progressBarThickness}px`,
        width: "100%",
        transition: "width 250ms",
        marginTop: theme.spacing(1),
      },
    },
    progressBar: {
      backgroundColor: progressBarActive,
      position: "absolute",
      top: 0,
      transition: "height 250ms",
      width: `${progressBarThickness}px`,
      zIndex: 1,
      [theme.breakpoints.down("md")]: {
        height: "100%",
        transition: "width 250ms",
      },
    },
    stepsContainer: {
      display: "flex",
      flexDirection: "column",
      height: "100%",
      justifyContent: "space-between",
      position: "absolute",
      [theme.breakpoints.down("md")]: {
        flexDirection: "row",
        width: "100%",
      },
    },
  };
});

const stepList: StepItem[] = [
  {
    stepKey: "Business Details",
    labelKey: "uiProgressStepperBusinessDetails",
    mobileLabelKey: "uiProgressStepperBusinessDetailsMobile",
  },
  {
    stepKey: "Payment Information",
    labelKey: "uiProgressStepperPaymentInformation",
    mobileLabelKey: "uiProgressStepperPaymentInformationMobile",
  },
  {
    stepKey: "Customer Service",
    labelKey: "uiProgressStepperCustomerSupport",
    mobileLabelKey: "uiProgressStepperCustomerSupportMobile",
  },
];

export const ProgressStepper: React.FC = () => {
  const isMobileWidth = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );
  const formCompletionState = Redux.useSelector(
    ({ formCompletion }: RootState) => formCompletion
  );
  const classes = useStyles();
  const t = useTranslation();

  const getProgressStyles = () => {
    const progressPercentage = getProgressPercentage(formCompletionState);
    const horizontal = {
      width: `${progressPercentage}%`,
    };
    const vertical = {
      height: `${progressPercentage}%`,
    };
    return isMobileWidth ? horizontal : vertical;
  };

  return (
    <Box className={classes.root} role="progressbar">
      <Box className={classes.progressContainer}>
        <Box className={classes.progressBar} style={getProgressStyles()} />
      </Box>
      <Box className={classes.stepsContainer}>
        {stepList.map((step) => (
          <Step
            key={step.stepKey}
            label={t(isMobileWidth ? step.mobileLabelKey : step.labelKey)}
            status={getStepStatus(formCompletionState, step.stepKey)}
            stepKey={step.stepKey}
          />
        ))}
      </Box>
    </Box>
  );
};
